import React from 'react';
import { Plus, Loader2, MapPin, Building2, Calendar } from 'lucide-react';
import type { Project } from '../../../project/types/project';
import { useProjectPagination } from '../../../project/hooks/useProjectPagination';
import { STATUS_COLORS } from '../../constants/filterConstants';
import { Button } from '../../../../components/ui/button';

interface ProjectGridProps {
  projects: Project[];
  onProjectClick: (project: Project) => void;
  setShowBackNavigation: (show: boolean) => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const ProjectGrid: React.FC<ProjectGridProps> = ({
  projects,
  onProjectClick,
  setShowBackNavigation,
}) => {
  const {
    displayedProjects,
    hasMore,
    isLoading,
    loadMore,
  } = useProjectPagination({ projects });

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
        {displayedProjects.map(project => (
          <div
            key={project.id}
            onClick={() => {
              onProjectClick(project);
              setShowBackNavigation(true);
            }}
            className="bg-white rounded-lg px-4 py-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-100"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-lg font-semibold text-[#4a6da7] mb-1">{project.name}</h3>
                <p className="text-gray-500 text-sm">#{project.number}</p>
              </div>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status] || STATUS_COLORS.default}`}>
                {project.status}
              </span>
            </div>
            <div className="space-y-2 mt-3">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm">{project.location}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm">{project.customer}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm">{formatDate(project.startDate)} - {formatDate(project.endDate)}</span>
              </div>
            </div>
            <div className="mt-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                {project.type}
              </span>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8">
          <Button
            onClick={loadMore}
            disabled={isLoading}
           // className="flex items-center gap-2 bg-white text-gray-700 px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
            Load More Projects
          </Button>
        </div>
      )}
    </>
  );
}; 